import React, { JSX } from 'react';
import { Avatar, Box, Theme } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import { JobMatchScore } from 'components/JobMatchAnalysis';
import { Candidate, Job } from 'types/types';
import { AnalysisState } from './jobAnalysisTypes';

function CandidateStepContent({
  isMobile,
  selectedCandidate,
}: {
  isMobile: boolean;
  selectedCandidate: Candidate | null;
}): JSX.Element {
  const flexDir = isMobile ? 'column' : 'row';
  return (
    <StepContentFrame title={`Candidate${!isMobile ? ' Selection' : ''}`}>
      {selectedCandidate && (
        <Box
          sx={{
            justifySelf: 'flex-start',
            flexDirection: flexDir,
            alignItems: 'center',
            fontSize: '0.75rem',
            gap: isMobile ? 0 : 1,
            width: '100%',
            minWidth: 0,
          }}
        >
          <Avatar
            src={
              selectedCandidate.profileImage
                ? `/api/1.0/candidates/profile/${selectedCandidate.username}`
                : ''
            }
            alt={`${selectedCandidate.fullName}'s profile`}
            sx={{
              alignSelf: isMobile ? 'center' : 'flex-start',
              width: 32,
              height: 32,
              border: '2px solid #e0e0e0',
            }}
          />
          <Box sx={{ flexDirection: 'column', textAlign: 'left', minWidth: 0, width: '100%' }}>
            {!isMobile && <Box>Name</Box>}
            <Box
              sx={{
                fontWeight: 'normal',
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                justifyContent: isMobile ? 'center' : 'flex-start',
              }}
              title={selectedCandidate.fullName}
            >
              {selectedCandidate.fullName}
            </Box>
          </Box>
        </Box>
      )}
    </StepContentFrame>
  );
}

function JobStepContent({
  isMobile,
  selectedJob,
}: {
  isMobile: boolean;
  selectedJob: Job | null;
}): JSX.Element {
  return (
    <StepContentFrame title={`Job${!isMobile ? ' Selection' : ''}`}>
      {selectedJob && (
        <Box
          sx={{
            justifySelf: 'flex-start',
            flexDirection: 'column',
            fontSize: '0.75rem',
            gap: isMobile ? 0 : 1,
            width: '100%',
            minWidth: 0,
          }}
        >
          <Box sx={{ flexDirection: 'column', textAlign: 'left', minWidth: 0 }}>
            {!isMobile && <Box>Company</Box>}
            <Box
              sx={{
                fontWeight: 'normal',
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                justifyContent: isMobile ? 'center' : 'flex-start',
              }}
              title={selectedJob.company}
            >
              {selectedJob.company}
            </Box>
          </Box>
          {!isMobile && (
            <Box sx={{ flexDirection: 'column', textAlign: 'left', minWidth: 0 }}>
              <Box>Title</Box>
              <Box
                sx={{
                  fontWeight: 'normal',
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={selectedJob.title}
              >
                {selectedJob.title}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </StepContentFrame>
  );
}

function AnalysisStepContent({
  isMobile,
  analysisState,
}: {
  isMobile: boolean;
  analysisState: AnalysisState;
}): JSX.Element {
  return (
    <StepContentFrame title={`${!isMobile ? 'Job ' : ''}Analysis`}>
      {analysisState.analysis && (
        <JobMatchScore
          sx={{ width: 'min-content', alignSelf: 'center' }}
          score={analysisState.analysis.score}
          variant="small"
        />
      )}
    </StepContentFrame>
  );
}

function ResumeStepContent({
  isMobile,
  savedResumeId,
  theme,
}: {
  isMobile: boolean;
  savedResumeId: string | null;
  theme: Theme;
}): JSX.Element {
  return (
    <StepContentFrame title={`${!isMobile ? 'Edit ' : ''}Resume`}>
      {savedResumeId && (
        <Box sx={{ justifyContent: 'center' }}>
          <ArticleIcon sx={{ fontSize: '0.9rem', color: theme.palette.success.main }} />
        </Box>
      )}
    </StepContentFrame>
  );
}

function StepContentFrame({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        '& div': { display: 'flex' },
        '& :first-of-type': { whiteSpace: 'nowrap' },
      }}
    >
      <Box sx={{ mb: 1, justifyContent: 'center' }}>{title}</Box>
      {children}
    </Box>
  );
}

export { AnalysisStepContent, CandidateStepContent, JobStepContent, ResumeStepContent };
